import { useState } from 'react';
import { FaCheck, FaCopy, FaDownload, FaEye } from 'react-icons/fa';
import { X } from 'lucide-react';
import { FaBarcode } from 'react-icons/fa6';
import { formatCurrency } from '../Utils';
import { BoletoModalProps } from '../Types';

const useCopyToClipboard = () => {
  const [isCopied, setIsCopied] = useState(false);
  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    });
  };
  return { isCopied, handleCopy };
};

function getPdfDataUrl(base64: string): string {
  const cleaned = base64.replace(/^data:application\/pdf;base64,/, '');
  return `data:application/pdf;base64,${cleaned}`;
}

function downloadPdf(base64: string, filename: string) {
  const dataUrl = getPdfDataUrl(base64);
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function viewPdf(base64: string) {
  const dataUrl = getPdfDataUrl(base64);
  window.open(dataUrl, '_blank', 'noopener,noreferrer');
}

export const BoletoModal: React.FC<BoletoModalProps> = ({
  isOpen,
  onClose,
  titulos,
  finalValue,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { isCopied, handleCopy } = useCopyToClipboard();

  if (!isOpen || !titulos.length) return null;

  const titulo = titulos[selectedIndex] ?? titulos[0];
  const linhaDigitavel = titulo.linha_digitavel ?? '';

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 animate-fade-in">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl p-6 sm:p-8 relative max-h-[95vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full p-2 transition"
        >
          <X size={22} />
        </button>

        <div className="flex flex-col items-center mb-4 sm:mb-6">
          <div className="flex flex-row items-center gap-4">
            <div className="bg-gradient-to-r from-amber-500 to-orange-600 text-white p-2 rounded-xl shadow-lg">
              <FaBarcode className="h-4 w-4" />
            </div>
            <h2 className="text-2xl font-extrabold text-gray-800">Boleto Gerado</h2>
          </div>
          <p className="text-gray-600 text-sm mt-2 text-center">
            Boleto no valor de{' '}
            <span className="font-bold text-amber-600">{formatCurrency(finalValue)}</span>
            {titulos.length > 1 ? ` (${titulos.length} parcelas)` : ''}.
          </p>
        </div>

        {titulos.length > 1 && (
          <div className="mb-4">
            <label className="text-xs font-semibold text-gray-600 block mb-2">Parcela</label>
            <select
              value={selectedIndex}
              onChange={(e) => setSelectedIndex(parseInt(e.target.value, 10))}
              className="w-full px-4 py-2 h-[42px] rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-1 focus:ring-amber-300"
            >
              {titulos.map((t, idx) => (
                <option key={t.titulo_id || idx} value={idx}>
                  Parcela {t.parcela ?? idx + 1}
                  {t.vencimento ? ` — venc. ${t.vencimento}` : ''}
                  {t.nosso_numero ? ` — ${t.nosso_numero}` : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        {linhaDigitavel && (
          <div className="mb-4">
            <label className="text-xs font-semibold text-gray-600 block mb-2">Linha digitável</label>
            <div className="relative">
              <input
                type="text"
                readOnly
                value={linhaDigitavel}
                className="w-full bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-3 pr-12 text-gray-700 text-xs focus:outline-none"
              />
              <button
                onClick={() => handleCopy(linhaDigitavel.replace(/\s/g, ''))}
                className={`absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg transition-all ${
                  isCopied
                    ? 'bg-emerald-100 text-emerald-600'
                    : 'bg-amber-100 hover:bg-amber-200 text-amber-600'
                }`}
                title={isCopied ? 'Copiado!' : 'Copiar linha digitável'}
              >
                {isCopied ? <FaCheck className="w-3 h-3" /> : <FaCopy className="w-3 h-3" />}
              </button>
            </div>
          </div>
        )}

        {titulo.pdf_base64 && (
          <div className="space-y-3">
            <iframe
              title={`Boleto ${selectedIndex + 1}`}
              src={getPdfDataUrl(titulo.pdf_base64)}
              className="w-full min-h-[420px] h-[min(70vh,720px)] border border-gray-200 rounded-xl"
            />
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => viewPdf(titulo.pdf_base64)}
                className="flex-1 flex items-center justify-center gap-2 bg-amber-500 text-white py-3 px-4 rounded-xl hover:bg-amber-600 transition-all font-bold"
              >
                <FaEye className="w-4 h-4" />
                Visualizar
              </button>
              <button
                onClick={() =>
                  downloadPdf(titulo.pdf_base64, `boleto-${titulo.nosso_numero || selectedIndex + 1}.pdf`)
                }
                className="flex-1 flex items-center justify-center gap-2 bg-gray-700 text-white py-3 px-4 rounded-xl hover:bg-gray-800 transition-all font-bold"
              >
                <FaDownload className="w-4 h-4" />
                Baixar PDF
              </button>
            </div>
          </div>
        )}

        <button
          onClick={onClose}
          className="mt-6 w-full bg-sky-500 text-white py-3 px-4 rounded-xl hover:bg-sky-600 transition-all font-bold"
        >
          Fechar
        </button>
      </div>
    </div>
  );
};
